interface SoftLockCandidate {
  readonly distance: number;
}

export interface SoftLockConfig {
  readonly engageDistance: number;
  readonly disengageDistance: number;
  readonly switchMargin: number;
}

/**
 * Resolves sticky snap behavior with engage/disengage hysteresis.
 *
 * The `config` controls three aspects:
 * - `engageDistance`: how close a new target must be to acquire a lock
 * - `disengageDistance`: how far the locked target can drift before the lock breaks
 * - `switchMargin`: how much closer a competing target must be to steal the lock
 */
export function resolveSoftLockedCandidate<TCandidate extends SoftLockCandidate>(
  lockedCandidate: TCandidate | null,
  bestCandidate: TCandidate | null,
  areSameTarget: (a: TCandidate, b: TCandidate) => boolean,
  wasPreviouslyLocked: boolean,
  config: SoftLockConfig,
): TCandidate | null {
  if (wasPreviouslyLocked && lockedCandidate != null && lockedCandidate.distance < config.disengageDistance) {
    if (
      bestCandidate != null &&
      !areSameTarget(lockedCandidate, bestCandidate) &&
      bestCandidate.distance + config.switchMargin < lockedCandidate.distance
    ) {
      return bestCandidate;
    }

    return lockedCandidate;
  }

  const acquireThreshold = wasPreviouslyLocked ? config.disengageDistance : config.engageDistance;
  if (bestCandidate != null && bestCandidate.distance < acquireThreshold) {
    return bestCandidate;
  }

  return null;
}
